import { type Row, flexRender } from '@tanstack/react-table'
import { useDrag, useDrop } from 'react-dnd'

import DragIndicatorIcon from '@/assets/icons/icon-drag-indicator.svg'
import type { Entry } from '@/components/ui/table/expandable-dnd-table/useTreeTable'
import { classNames } from '@/utils/helpers'

import { TableCell, TableRow } from './../table.tsx'

export interface DraggableRowProps<T> {
  row: Row<Entry<T>>
  onMove: (origin: Row<Entry<T>>, target: Row<Entry<T>>) => void
  rowName: string
  disableDrag?: boolean
}

// eslint-disable-next-line react/function-component-definition
export function DraggableRow<T>({
  row,
  onMove,
  rowName,
  disableDrag,
}: DraggableRowProps<Entry<T>>) {
  const [{ isActive }, dropRef] = useDrop({
    accept: rowName,
    drop: (origin: Row<Entry<T>>) => onMove(origin, row),
    collect: (monitor) => ({
      isActive: monitor.canDrop() && monitor.isOver(),
    }),
  })

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    item: () => row,
    type: rowName,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const renderRow = row.getVisibleCells().map((cell, index) => {
    if (index === 0 && !disableDrag) {
      return (
        <TableCell
          key={cell.id}
          ref={dropRef}
        >
          <div
            className="flex flex-row items-center gap-3"
            style={{
              paddingLeft: `${row.depth * 2}rem`,
            }}
          >
            <div ref={dragRef}>
              <DragIndicatorIcon className="h-4 w-4 cursor-move" />
            </div>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        </TableCell>
      )
    }

    return (
      <TableCell key={cell.id}>
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    )
  })

  return (
    <TableRow
      ref={previewRef}
      className={classNames(
        isActive && 'bg-primary-50 dark:bg-primary-500/10',
        isDragging && 'bg-primary-500/20',
      )}
    >
      {renderRow}
    </TableRow>
  )
}
